// Rule-based reading of contract text: no AI, runs on the device (and on the server for emailed
// documents). It finds the type, title, dates, notice and renewal, parties, value and key terms,
// each with the passage it came from. The pieces live in ./rules; ./parties and ./amounts too.
// Everything returned is a suggestion the user confirms; nothing is saved automatically.
import { findAmounts, PERIOD_LABEL, periodsInTerm, pickContractAmount } from "./amounts";
import type { RenewalType } from "./contract";
import { diffDays } from "./dates";
import { findParties, pickCounterparty, type Party } from "./parties";
import { classify } from "./rules/classify";
import { findDates as findDatesIn, findTerm, inferDateOrder, roleDates, termEnd, wholeMonths, type DateOrder, type RoledDate } from "./rules/dates";
import { findNotices, mainNotice } from "./rules/notice";
import { DURATION, parseNumberWords as parseWords, quantity, toMonths, unitOf } from "./rules/numbers";
import { findTerms } from "./rules/terms";
import { evidence, labelLines, normalize, sentences } from "./rules/text";

export type { DateOrder };

export interface Suggestion<T> {
  value: T;
  /** The text the suggestion came from, so the user can check it. */
  evidence: string;
  /** How the value was worked out, when it isn't written in the document as is ("$4,500 per month × 6 months"). */
  note?: string;
}

export interface Suggestions {
  /** Catalogue type id. `confident` when it's clearly this type; otherwise it's only offered. */
  type?: Suggestion<string> & { confident: boolean };
  startDate?: Suggestion<string>;
  endDate?: Suggestion<string>;
  noticePeriodDays?: Suggestion<number>;
  renewalType?: Suggestion<RenewalType>;
  renewalTermMonths?: Suggestion<number>;
  /** The document says the contract runs indefinitely. */
  noEndDate?: Suggestion<true>;
  parties?: Suggestion<Party[]>;
  counterparty?: Suggestion<string>;
  value?: Suggestion<{ minor: number; currency: string }>;
  /** A recurring price ("$150.00 per hour"), kept even when no total can be worked out. */
  rate?: Suggestion<string>;
  /** The document's own heading, e.g. "Mutual Non-Disclosure Agreement". */
  title?: Suggestion<string>;
  /** Key terms by catalogue key ("monthly_rent" → "25000"), as the form stores them. */
  details?: Record<string, Suggestion<string>>;
  /** The currency the document's amounts are in, for amounts among the key terms. */
  currency?: Suggestion<string>;
}

/** "forty-five" → 45, "one hundred twenty" → 120, "60" → 60. */
export function parseNumberWords(input: string): number | null {
  return parseWords(input);
}

/** Every date in the text, in the formats contracts actually use. */
export function findDates(text: string, order: DateOrder = "dmy") {
  return findDatesIn(text, order);
}

const NO_END_DATE =
  /\b(?:(?:remain|continue)\s+in\s+(?:full\s+)?(?:force|effect)(?:\s+and\s+effect)?\s+indefinitely|indefinite\s+(?:term|period|duration)|in\s+perpetuity|no\s+(?:fixed\s+|set\s+)?(?:expiration|expiry|end|termination)\s+date|(?:shall|will)\s+(?:continue|remain\s+in\s+(?:full\s+)?(?:force|effect)(?:\s+and\s+effect)?)\s+until\s+terminated|perpetual\s+(?:term|agreement|licen[cs]e)|(?:term|agreement)\s+(?:shall\s+be|is)\s+perpetual|\bat[\s-]will\b)/i;

const HEADING = /\b(?:agreement|contract|lease|deed|licen[cs]e|order|addendum|amendment|memorandum|terms|nda|mou|statement\s+of\s+work|letter|policy|engagement|schedule|membership|form|offer|undertaking|declaration|bond|will|guarantee)\b/i;
// Stamp-paper certificates, letterheads and reference lines that come before the real title.
const NOT_TITLE = /^(?:india\s+non\s+judicial|government\s+of|e-?stamp|certificate|account\s+reference|unique\s+doc|purchased\s+by|description\s+of|property\s+description|consideration\s+price|first\s+party|second\s+party|stamp\s+duty|cin\b|regd\.?|registered\s+office|irdai|tel\b|phone|e-?mail|www\.|ref\b|to,?$|dear\b|date\b|page\b)/i;

function titleCaseHeading(heading: string): string {
  if (heading !== heading.toUpperCase()) return heading;
  return heading
    .toLowerCase()
    .replace(/(^|[\s(-])(\p{L})/gu, (_, a: string, c: string) => a + c.toUpperCase())
    .replace(/(?<!^)\b(Of|And|The|For|To|In|On|A|An|Cum)\b/g, (w) => w.toLowerCase())
    .replace(/\b(Nda|Mou|Amc|Llp|Sow|Po)\b/g, (w) => w.toUpperCase());
}

/** The document's heading: a short line near the top naming the kind of document, or a letter's subject. */
function findTitle(lines: readonly string[]): Suggestion<string> | null {
  const subject = lines.slice(0, 40).map((l) => /^(?:sub(?:ject)?|re)\s*[:.-]\s*(.{4,100})$/i.exec(l)?.[1]).find(Boolean);
  const candidates = lines.slice(0, 30).filter((l) => l.length >= 4 && l.length <= 120 && !NOT_TITLE.test(l) && !/[:;,]$|\.$/.test(l) && !/:\s/.test(l));
  for (const line of candidates) {
    // "IRONHOUSE FITNESS CLUB - MEMBERSHIP AGREEMENT": the part after the dash is the title.
    const parts = line.split(/\s+[-–—|]\s+/);
    const part = parts.length > 1 && HEADING.test(parts.at(-1)!) ? parts.at(-1)! : line;
    if (HEADING.test(part) && part.split(/\s+/).length <= 12 && !/^(?:this|the|in\s+witness|whereas|now|we|you|i)\b/i.test(part)) {
      return { value: titleCaseHeading(part.trim()), evidence: line };
    }
  }
  return subject ? { value: titleCaseHeading(subject.trim()), evidence: subject } : null;
}

export interface SuggestOptions {
  /** The user's own organisation and name, so the other party can be told apart. */
  ownNames?: readonly string[];
}

// Strongest first; at equal strength a term's own start beats an effective date.
const strongest = (dates: RoledDate[], roles: string[], after?: string) =>
  dates
    .filter((d) => roles.includes(d.role) && (!after || d.date > after))
    .sort((a, b) => b.strength - a.strength || Number(!!a.effective) - Number(!!b.effective) || a.index - b.index)[0];

/** `order` is how the reader writes dates; the document's own dates and currency come first. */
export function suggestFromText(rawText: string, readerOrder: DateOrder = "dmy", options: SuggestOptions = {}): Suggestions {
  const doc = normalize(typeof rawText === "string" ? rawText : "");
  const text = doc.flat;
  const out: Suggestions = {};
  if (!text.trim()) return out;
  const order = inferDateOrder(text, readerOrder);
  const sents = sentences(text, doc.breaks);
  const labels = labelLines(doc.lines);

  const title = findTitle(doc.lines);
  if (title) out.title = title;
  const guess = classify(text, title?.value ?? null, { ownNames: options.ownNames });
  if (guess) out.type = { value: guess.id, evidence: guess.evidence, confident: guess.confident };

  // --- dates and term ---------------------------------------------------------------------
  const dates = roleDates(text, findDatesIn(text, order));
  const ev = (d: RoledDate) => evidence(text, d.index, d.text.length);
  const startDate = strongest(dates, ["start", "joining"]) ?? strongest(dates, ["signed"]);
  if (startDate) out.startDate = { value: startDate.date, evidence: ev(startDate) };
  const endDate = strongest(dates, ["end"], startDate?.date) ?? strongest(dates, ["last_emi"], startDate?.date);
  if (endDate) out.endDate = { value: endDate.date, evidence: ev(endDate) };
  const term = findTerm(text);
  if (!out.endDate && out.startDate && term) {
    const signed = strongest(dates, ["signed"]);
    const from = term.fromSigning && signed ? signed.date : out.startDate.value;
    out.endDate = { value: termEnd(from, term.months), evidence: evidence(text, term.index, term.length), note: `${term.months} months from ${from}` };
  }
  if (!out.endDate) {
    const forever = NO_END_DATE.exec(text);
    if (forever) out.noEndDate = { value: true, evidence: evidence(text, forever.index, forever[0].length) };
  }
  const termMonths = out.startDate && out.endDate ? wholeMonths(out.startDate.value, out.endDate.value) : (term?.months ?? null);

  // --- notice and renewal ---------------------------------------------------------------------
  const notices = findNotices(sents);
  const notice = mainNotice(notices);
  if (notice) out.noticePeriodDays = { value: notice.days, evidence: evidence(text, notice.index, notice.length) };

  const auto = /\b(?:automatic(?:ally)?\s+(?:be\s+)?renew\w*|auto[-\s]?renew\w*|automatic\s+renewal|renew(?:s|ed)?\s+automatically|(?:stand|be\s+deemed\s+(?:to\s+(?:be|have\s+been)\s+)?|deemed\s+(?:to\s+(?:be|have\s+been)\s+)?)renewed|shall\s+renew|will\s+renew|successive\s+(?:renewal\s+)?(?:terms|periods)|evergreen)\b/i.exec(text);
  const autoNegated = auto && /\bnot\s+(?:be\s+)?(?:automatically\s+)?renew|\bnot\s+renew\s+automatically|\bno\s+automatic\s+renewal/i.test(text.slice(Math.max(0, auto.index - 40), auto.index + auto[0].length + 15));
  const manual = /\b(?:may|can|shall|could)\s+be\s+(?:renewed|extended)\b[^.;]{0,100}?\b(?:mutual\w*|option|agree\w*|consent|request|discretion|writing)|\boption\s+to\s+renew\b|\b(?:renewed|extended)\s+(?:with|by|upon|on)\s+(?:the\s+)?(?:mutual\s+|written\s+)?(?:consent|agreement)\b|\brenewable\s+(?:at|by|on|upon)\b|\brenew\w*\s+(?:on|by|upon|with)\s+mutual\b|\bextended\s+by\s+mutual\b|\bsubject\s+to\s+renewal\b/i.exec(text);
  if (auto && !autoNegated) out.renewalType = { value: "auto", evidence: evidence(text, auto.index, auto[0].length) };
  else if (manual) out.renewalType = { value: "manual", evidence: evidence(text, manual.index, manual[0].length) };
  if (out.renewalType) {
    const renewalTermRe = new RegExp(`(?:successive|additional|renewal|further)\\s+(?:renewal\\s+)?(?:terms?|periods?)\\s+of\\s+${DURATION}|successive\\s+${DURATION.replace("\\s*[-\\s]?\\s*", "[-\\s]*")}\\s*(?:renewal\\s+)?(?:terms?|periods?)`, "iu");
    const rt = renewalTermRe.exec(text);
    const months = rt ? toMonths(quantity(rt[1] ?? rt[3]!) ?? 0, unitOf(rt[2] ?? rt[4]!)) : null;
    if (rt && months) out.renewalTermMonths = { value: months, evidence: evidence(text, rt.index, rt[0].length) };
    else if (out.renewalType.value === "auto" && termMonths) out.renewalTermMonths = { value: termMonths, evidence: term ? evidence(text, term.index, term.length) : out.renewalType.evidence };
  }

  // --- parties -------------------------------------------------------------------------------
  const found = findParties(text, doc.raw);
  let counterparty: string | null = null;
  if (found) {
    out.parties = { value: found.parties, evidence: found.evidence };
    const pick = pickCounterparty(found.parties, options.ownNames);
    if (pick) {
      const why =
        pick.reason === "own-name" ? "the party that isn't your organisation" : pick.reason === "role" && pick.party.role ? `the ${pick.party.role.toLowerCase()} in the contract` : "check this is the other side";
      out.counterparty = { value: pick.party.name, evidence: found.evidence, note: why };
      counterparty = pick.party.name;
    }
  }

  // --- value: a one-off amount, or a recurring one multiplied out over the term ----------------------
  const amounts = findAmounts(text);
  const amount = pickContractAmount(amounts);
  if (amount) {
    const ev2 = evidence(text, amount.index, amount.length);
    if (!amount.period) {
      out.value = { value: { minor: amount.minor, currency: amount.currency }, evidence: ev2 };
    } else {
      const s = out.startDate?.value;
      const e = out.endDate?.value;
      const days = s && e ? diffDays(s, e) + 1 : null;
      const n = periodsInTerm(amount.period, termMonths, days);
      const rate = `${amount.text} per ${PERIOD_LABEL[amount.period]}`;
      out.rate = { value: rate, evidence: ev2 };
      if (n && amount.minor * n <= 1e15) {
        out.value = { value: { minor: amount.minor * n, currency: amount.currency }, evidence: ev2, note: `${rate} × ${n} ${PERIOD_LABEL[amount.period]}${n === 1 ? "" : "s"}` };
      }
    }
  }

  const currencies = amounts.map((a) => a.currency);
  const currency = [...new Set(currencies)].sort((a, b) => currencies.filter((c) => c === b).length - currencies.filter((c) => c === a).length)[0];
  if (currency) {
    const first = amounts.find((a) => a.currency === currency)!;
    out.currency = { value: currency, evidence: evidence(text, first.index, first.length) };
  }

  // --- key terms --------------------------------------------------------------------------------
  const details = findTerms({
    doc, sents, labels, type: guess?.id ?? null, amounts, dates, notices, term, termMonths,
    startDate: out.startDate?.value ?? null, parties: found?.parties ?? [], counterparty, order,
  });
  if (Object.keys(details).length) out.details = details;
  return out;
}

export function dateOrderForLocale(locale: string | undefined): DateOrder {
  return /^en-(US|PH)|^(fil|es-US)/i.test(locale ?? "") ? "mdy" : "dmy";
}
